import React, { forwardRef } from 'react';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';


export interface JcBoxProps extends JcCommonProps {
  spacing?: boolean;
  alignCenter?: boolean;
  justifyCenter?: boolean;
  flexGrow?: boolean;
  /** ARIA role when the box is a semantic unit (e.g. "group" for a control cluster). */
  role?: React.AriaRole;
  /** Accessible name for the box; pair with `role`. */
  ariaLabel?: string;
  /**
   * Fires when focus leaves the box entirely (not when moving between its own
   * children) — for dismissing a transient control once the user clicks or tabs
   * away. Requires a focused element inside the box. Unlike the raw DOM `blur`,
   * internal focus moves don't trigger it. `relatedTarget` is null when focus
   * was dropped rather than moved (e.g. macOS browsers don't focus a clicked
   * button), so a null check can't tell inside from outside clicks.
   */
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  children: React.ReactNode;
}

export const JcBox = forwardRef<HTMLDivElement, JcBoxProps>((props: JcBoxProps, ref) => {

  const commonProps = JcUtils.commonProps(props, [
    props.spacing && "jc-spacing",
    props.alignCenter && "jc-align-center",
    props.justifyCenter && "jc-justify-center",
    props.flexGrow && "jc-flex-grow",
    "jc-box",
  ]);

  const onBlur = props.onBlur;
  const handleBlur = onBlur
    ? (event: React.FocusEvent<HTMLDivElement>) => {
      // relatedTarget is the element gaining focus. If it's still inside the box
      // we're moving between children, so this isn't a "left the group" blur.
      if (!event.currentTarget.contains(event.relatedTarget)) onBlur(event);
    }
    : undefined;

  return <div ref={ref} {...commonProps} role={props.role} aria-label={props.ariaLabel} onBlur={handleBlur}>
    {props.children}
  </div>
})

export const JcRow = forwardRef<HTMLDivElement, JcBoxProps>((props: JcBoxProps, ref) => {

  const commonProps = JcUtils.commonProps(props, ["jc-row"]);
  const boxProps = JcUtils.otherProps(props);

  return <JcBox ref={ref} {...boxProps} {...commonProps} />
})

export const JcColumn = forwardRef<HTMLDivElement, JcBoxProps>((props: JcBoxProps, ref) => {

  const commonProps = JcUtils.commonProps(props, ["jc-column"]);
  const boxProps = JcUtils.otherProps(props);

  return <JcBox ref={ref} {...boxProps} {...commonProps} />
})